/**
 * 筛选分班 API（balanced-grouping change 5-2，ADM-17/18）。
 *
 * 对齐后端 classes BC 分班域的 REST 契约：
 * - GET  /placements/suggestions        -> PlacementSuggestions（五维筛选；候选/分组/溢出/统计/提示）
 * - GET  /placements/students/search    -> PlacementSearchStudent[]（添加候选联想搜索）
 * - POST /placements/preview            -> PlacementPreview（建班四要素自动生成，只读）
 * - POST /placements/confirm            -> PlacementConfirmResult（RECRUITING 建班 + 批量入班逐生明细）
 * 失败学生补入既有班级复用 classesApi.enrollMember（POST /classes/:id/members，四步校验链）。
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 候选学生（候选池/分组/溢出共用）。 */
export interface PlacementCandidate {
  studentId: string;
  studentNo: string;
  name: string;
  gender: string;
  gradeStage: string;
  /** 未录入为 null（页面展示「未录入」）。 */
  score: number | null;
  homeCampusId: string;
  targetSkuId: string;
  targetSkuName: string;
}

/** 建议分组块（序号自 1 起全局连续）。 */
export interface PlacementGroup {
  index: number;
  skuId: string;
  skuName: string;
  /** 本组容量（= SKU 每班最多人数）。 */
  capacity: number;
  students: PlacementCandidate[];
}

/** SKU 建议汇总（分列统计）。 */
export interface PlacementSkuSummary {
  skuId: string;
  skuName: string;
  candidateCount: number;
  suggestedClassCount: number;
  maxClassCapacity: number;
  maxClassCount: number;
}

/** 分班建议响应（空结果时三数组为空，不报错）。 */
export interface PlacementSuggestions {
  totalCandidates: number;
  groups: PlacementGroup[];
  overflowStudents: PlacementCandidate[];
  skuSummaries: PlacementSkuSummary[];
  /** 不可用 SKU 提示（DISABLED / 无分班配置）。 */
  notices: string[];
}

/** 添加候选搜索结果项。 */
export interface PlacementSearchStudent {
  studentId: string;
  studentNo: string;
  name: string;
  gender: string;
  gradeStage: string;
  score: number | null;
  /** 该科目剩余有效课时合计。 */
  remainingHours: number;
}

/** 确认预览响应（服务端自动生成的建班四要素，可修改后提交）。 */
export interface PlacementPreview {
  className: string;
  skuId: string;
  skuName: string;
  teacherId: string;
  teacherName: string;
  defaultClassroomId: string;
  classroomName: string;
  startDate: string;
  endDate: string;
  capacity: number;
  studentCount: number;
}

/** 逐生确认结果（部分成功语义）。 */
export interface PlacementConfirmItem {
  studentId: string;
  success: boolean;
  reason?: string;
}

/** 最终确认响应。 */
export interface PlacementConfirmResult {
  classId: string;
  className: string;
  status: string;
  results: PlacementConfirmItem[];
}

/** 建议查询参数（五维筛选，科目必选；分数区间直传，与档位结构解耦）。 */
export interface PlacementSuggestQuery {
  campusId?: string;
  subjectId: string;
  gradeStage?: string;
  /** 分数区间下界（含，须与 scoreMax 成对提交）。 */
  scoreMin?: number;
  /** 分数区间上界（含，须与 scoreMin 成对提交）。 */
  scoreMax?: number;
  /** 仅筛该科目未录入分数（与区间互斥）。 */
  scoreNone?: boolean;
  gender?: string;
}

function buildSuggestQuery(query: PlacementSuggestQuery): string {
  const params = new URLSearchParams();
  if (query.campusId) params.set('campusId', query.campusId);
  params.set('subjectId', query.subjectId);
  if (query.gradeStage) params.set('gradeStage', query.gradeStage);
  if (query.scoreMin !== undefined) params.set('scoreMin', String(query.scoreMin));
  if (query.scoreMax !== undefined) params.set('scoreMax', String(query.scoreMax));
  if (query.scoreNone !== undefined) params.set('scoreNone', String(query.scoreNone));
  if (query.gender) params.set('gender', query.gender);
  return `${ApiPaths.placements.suggestions}?${params.toString()}`;
}

async function getSuggestions(
  query: PlacementSuggestQuery,
): Promise<PlacementSuggestions> {
  return http.get<PlacementSuggestions>(buildSuggestQuery(query));
}

async function searchStudents(params: {
  subjectId: string;
  campusId?: string;
  keyword: string;
  limit?: number;
}): Promise<PlacementSearchStudent[]> {
  const qs = new URLSearchParams();
  qs.set('subjectId', params.subjectId);
  if (params.campusId) qs.set('campusId', params.campusId);
  qs.set('keyword', params.keyword);
  if (params.limit) qs.set('limit', String(params.limit));
  return http.get<PlacementSearchStudent[]>(
    `${ApiPaths.placements.studentsSearch}?${qs.toString()}`,
  );
}

async function previewPlacement(body: {
  skuId: string;
  campusId: string;
  studentIds: string[];
}): Promise<PlacementPreview> {
  return http.post<PlacementPreview>(ApiPaths.placements.preview, body);
}

async function confirmPlacement(body: {
  skuId: string;
  campusId: string;
  className: string;
  teacherId: string;
  defaultClassroomId: string;
  startDate: string;
  endDate: string;
  studentIds: string[];
}): Promise<PlacementConfirmResult> {
  return http.post<PlacementConfirmResult>(ApiPaths.placements.confirm, body);
}

/** 筛选分班 API 唯一对外入口。 */
export const placementsApi = {
  getSuggestions,
  searchStudents,
  previewPlacement,
  confirmPlacement,
};
